import { queryOptions, skipToken, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import type {
  ReturnsReturnProcessIdentification,
  ReturnsTrackingInfoResponseLogisticsInfoLabelStatusEnum
} from '@itsrever/rever-api'
import { returnsApiProxy } from '@/redux/api/returns-api-slice'

// Labels are often generated minutes after the process starts, so poll until
// one shows up instead of asking the shopper to refresh the page.
const LABEL_POLL_INTERVAL_MS = 30_000
// Ten minutes covers most labels; later ones still arrive by email.
const LABEL_MAX_POLLS = 20

interface LabelURLOptions {
  enabled?: boolean
  poll?: boolean
}

// Only a label the backend reports as pending, for a parcel the shopper still
// has, is worth waiting for.
export const shouldPollLabel = (
  labelStatus:
    | ReturnsTrackingInfoResponseLogisticsInfoLabelStatusEnum
    | undefined,
  isParcelCollected: boolean
) => labelStatus === 'PENDING' && !isParcelCollected

export const getLabelURLQuery = (
  { email, process_id }: Partial<ReturnsReturnProcessIdentification>,
  { enabled = true, poll = false }: LabelURLOptions = {}
) =>
  queryOptions({
    queryKey: ['label-url', email, process_id],
    queryFn:
      enabled && email !== undefined && process_id !== undefined
        ? async () => {
            try {
              const { data } = await returnsApiProxy.labelPresignedURL({
                returnsLabelPresignedURLRequest: {
                  return_process_identification: { email, process_id }
                }
              })

              return data.url ?? null
            } catch (error) {
              // A label that does not exist yet answers 404. That is the normal
              // "still being generated" state, not a failure to report.
              if (axios.isAxiosError(error) && error.response?.status === 404) {
                return null
              }
              throw error
            }
          }
        : skipToken,
    staleTime: Infinity,
    retry: false,
    refetchInterval: (query) =>
      poll &&
      !query.state.data &&
      query.state.status !== 'error' &&
      query.state.dataUpdateCount < LABEL_MAX_POLLS
        ? LABEL_POLL_INTERVAL_MS
        : false
  })

export const useLabelURL = (
  identification: Partial<ReturnsReturnProcessIdentification>,
  options?: LabelURLOptions
) => {
  return useQuery(getLabelURLQuery(identification, options))
}
